<template>
  <div
    :class="[
      'm-1 overflow-hidden rounded',
      isActive ? 'border border-primary-300 bg-primary-50 dark:border-primary-700 dark:bg-primary-800/20' : ''
    ]"
  >
    <radio-field
      v-model="_value"
      :value="item"
      option-class="flex flex-row-reverse items-center justify-between p-2 cursor-pointer"
      option-class-active=""
    >
      <div class="w-full flex items-center justify-between">
        <div class="flex-grow flex-shrink flex space-x-2 items-start pr-2 w-full text-sm leading-none" :class="[ isActive ? 'text-primary-700 dark:text-primary-300' : 'text-gray-700 dark:text-gray-200' ]">
          <div>
            <p class="font-semibold">
              {{ $date.minutes(item.start) }}
            </p>
          </div>
          <p>
            -
          </p>
          <div>
            <p>
              {{ item.employeeLabel }}
            </p>
            <p v-if="item.hasResources" class="text-gray-500 dark:text-gray-400 pt-1">
              {{ $t('components.reservation.availability.auto.slot.includesResources') }}
            </p>
          </div>
        </div>

        <div class="flex items-center flex-grow flex-shrink mr-2">
          <o-chip v-if="!!item.totalDelay" variant="warning" flat size="xs" class="block">
            <span class="whitespace-nowrap">{{ $t('components.reservation.availability.auto.slot.totalDelay', { delay: item.totalDelay }) }}</span>
          </o-chip>

          <o-chip v-if="isCurrent" variant="success" size="xs" class="block">
            <span class="whitespace-nowrap">{{ $t('general.current') }}</span>
          </o-chip>

          <o-chip v-if="isFull" variant="danger" size="xs" class="block">
            <span class="whitespace-nowrap">{{ $t('general.full') }}</span>
          </o-chip>
        </div>
      </div>
    </radio-field>

    <transition
      name="expand"
      @enter="enter"
      @after-enter="afterEnter"
      @leave="leave"
    >
      <div v-if="isActive">
        <div>
          <div class="p-2 bg-primary-50 dark:bg-primary-800/20">
            <div v-for="(service, serviceIndex) in item.services" :key="serviceIndex" class="text-sm text-gray-700 bg-white/25 border border-primary-200 rounded p-2 mb-2 last:mb-0 dark:text-gray-200 dark:bg-gray-900 dark:border-primary-800">
              <div class="flex items-center">
                <div class="hidden md:block self-stretch relative pr-3">
                  <div
                    class="w-1 rounded h-full"
                    :style="{
                      backgroundColor: '#' + service.service.borderHexColour
                    }"
                  />
                </div>
                <p class="pr-6 font-medium w-24">
                  {{ $date.minutes(service.startTime) }}
                </p>
                <p class="pr-6" v-html="$t('components.reservation.availability.auto.slot.service', { title: service.service.title })" />
                <p v-html="$t('components.reservation.availability.auto.slot.employee', { name: service.employee.displayName })" />
              </div>
              <div v-if="service.resources && service.resources.length" class="flex items-center">
                <div class="pr-6 w-24 ml-4 flex-shrink-0" />
                <div class="text-sm text-gray-700 mt-2 dark:text-gray-200">
                  <span v-for="resource in service.resources" :key="resource.serviceResource.idServiceResource" class="mr-2">
                    <span class="text-sm text-gray-500 dark:text-gray-400">{{ $t('general.colon', { label: resource.serviceResource.name }) }}</span>
                    <span v-for="(resourceSlot, index) in resource.resources" :key="resourceSlot.idEmployee">
                      <template v-if="index != 0">, </template>
                      {{ resourceSlot.displayName }}
                    </span>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { RadioField } from '@/components/fields'

interface Props {
  modelValue?: object | null
  item: Record<string, any>
  move?: object | null
}

interface Emits {
  (e: 'update:modelValue', value: any): void
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: null,
  move: null,
})

const emit = defineEmits<Emits>()

const _value = computed({
  get () {
    return props.modelValue
  },
  set (value) {
    emit('update:modelValue', value)
  },
})

const employees = computed(() => {
  const emps = props.item.services
    .map(({ employee }: any) => {
      return employee.displayName
    })

  return Array.from(new Set(emps))
    .join(', ')
})

const isActive = computed(() => _value.value === props.item)
const isMove = computed(() => !!props.move)

const isFull = computed(() => {
  const slot = props.item
  const service = slot.services?.[0]

  if (service && service.customerMax) {
    const customerMax = service.customerMax
    const customerCount = service.customerCount
    return customerCount >= customerMax
  }

  return false
})

const isCurrent = computed(() => {
  if (isMove.value) {
    const slot = props.item
    const reservation = props.move as any

    const reservationStart = Number(reservation?.startTime?.value)
    const slotStart = slot?.startTime

    return reservationStart === slotStart
  }

  return false
})

function afterEnter (el: HTMLElement) {
  el.style.height = 'auto'
}

function enter (el: HTMLElement) {
  requestAnimationFrame(() => {
    el.style.height = el.scrollHeight + 'px'
  })
}

function leave (el: HTMLElement) {
  el.style.height = el.scrollHeight + 'px'
  requestAnimationFrame(() => {
    el.style.height = '0'
  })
}
</script>
